(() => {
  'use strict';

  if (customElements.get('parallax-element')) return;

  class ParallaxElement extends HTMLElement {
    constructor() {
      super();
      this.container = this.parentNode;
      this.percentage = 0;
      this.percentageFull = 0;
      this.animation = this.getAttribute('data-parallax-animation');
      this.animations = this.animation.split(',');
      this.disableOnMobile = this.hasAttribute('data-parallax-disable-on-mobile');
      this.viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
      this.containerHeight = this.container.offsetHeight;
      this.main = document.querySelector('[data-main]');
      this.firstSection = this.main.children[0];
      this.orientation = window.theme.getScreenOrientation();
      this.lastScrollTop = null;
      this.cachedStyles = new Map();
      this.cardScrollingHidden = null;
      this.footerVisible = null;
      this.stickyTopZero = null;
      this.scrollEvent = () => this.updateParallax();
      this.resizeEvent = () => this.updateHeight();
      this.refreshEvent = () => this.refresh();
    }

    setStyleProperty(property, value) {
      if (this.cachedStyles.get(property) === value) return;
      this.cachedStyles.set(property, value);
      this.container.style.setProperty(property, value);
    }

    setClassState(element, className, state, cacheProperty) {
      if (!element || this[cacheProperty] === state) return;
      this[cacheProperty] = state;
      element.classList.toggle(className, state);
    }

    assignFooterArguments() {
      if (!this.animations.includes('footer')) return;
      this.shopifySection = this.closest('.shopify-section');
      this.footerPrevSectionOffset = 0;
      if (this.hasAttribute('data-parallax-rounded-corners')) {
        this.footerPrevSectionOffset = this.hasAttribute('data-parallax-rounded-corners-large') ? 34 : 8;
      }
      this.prevSection = this.container.parentNode.previousElementSibling;
      if (!this.prevSection || this.prevSection.classList.contains('shopify-section') || this.prevSection.classList.contains('main-content')) {
        this.prevSection = this.main;
      }
      this.containerHeight = this.container.offsetHeight;
      this.footerOffset = 0.15 * this.containerHeight;
      this.cachedPosition = this.footerOffset;
      this.cachedPercentage = 0;
    }

    assignCardScrollingArguments() {
      if (!this.animations.includes('card-scrolling')) return;
      const {stickyHeaderHeight} = window.theme.readHeights();
      this.headerHeight = stickyHeaderHeight || 0;
      this.shopifySection = this.closest('.shopify-section');
      this.nextSection = this.shopifySection.nextElementSibling;
      this.cardScrollingStickyTop = this.headerHeight || 0;
      this.cardScrollingMarginTop = this.nextSection
        ? parseInt(window.getComputedStyle(this.nextSection).getPropertyValue('margin-top'))
        : 0;
      this.after = false;
      this.prevSection = this.shopifySection.previousElementSibling;
      this.updateStickyTopClass();
      this.opacityValue = (value) => {
        if (!value || value <= 0) return 0;
        if (value <= 100) return Number(value / 100).toFixed(4);
        return 1;
      };
    }

    updateStickyTopClass() {
      if (!this.prevSection) return;
      const isFirstSection = this.prevSection.matches(`#${this.firstSection.id}`);
      const previousSectionHeight = this.prevSection.offsetHeight;
      this.setClassState(
        this.shopifySection,
        'sticky-top-zero',
        isFirstSection && previousSectionHeight <= 2 * this.headerHeight,
        'stickyTopZero'
      );
    }

    connectedCallback() {
      this.assignFooterArguments();
      this.assignCardScrollingArguments();
      requestAnimationFrame(() => {
        this.updateParallax(true);
        document.addEventListener('theme:scroll', this.scrollEvent);
        document.addEventListener('theme:resize', this.resizeEvent);
        document.addEventListener('theme:parallax:refresh', this.refreshEvent);
      });
    }

    updateParallax(force = false) {
      if (this.container.classList.contains('is-disabled') || (this.disableOnMobile && window.theme.isMobile())) return;
      const scrollTop = Math.round(window.scrollY);
      if (!force && scrollTop === this.lastScrollTop) return;
      this.lastScrollTop = scrollTop;
      this.scrollTop = scrollTop;
      this.scrollBottom = scrollTop + this.viewportHeight;
      this.elementOffsetTopPoint = Math.round(this.container.getBoundingClientRect().top + scrollTop);
      if (window.Shopify.designMode) {
        this.assignCardScrollingArguments();
        this.containerHeight = this.container.offsetHeight;
      }
      this.elementOffsetBottomPoint = this.elementOffsetTopPoint + this.containerHeight;
      this.isBottomOfElementPassed = this.elementOffsetBottomPoint < scrollTop;
      this.isTopOfElementReached = this.elementOffsetTopPoint < this.scrollBottom;
      this.isInView = this.isTopOfElementReached && !this.isBottomOfElementPassed;
      this.adjustCalculations();
      this.toggleVisibility();
      if (!this.isInView) return;

      this.scrollProgress = this.scrollBottom - this.elementOffsetTopPoint;
      this.percentage = 100 * (this.scrollProgress - this.containerHeight / 2) / this.viewportHeight;
      this.percentageFull = Number(this.scrollProgress / this.containerHeight).toFixed(2);
      this.percentageVisible = Number(100 * this.scrollProgress / (this.viewportHeight + this.containerHeight)).toFixed(2);
      if (this.animations.includes('circle')) this.animateCircleText();
      if (this.animations.includes('horizontal') || this.animations.includes('vertical') || this.animations.includes('diagonal')) this.animateOverlappingImages();
      if (this.animations.includes('offset-ltr') || this.animations.includes('offset-rtl')) this.offsetX();
      if (this.animations.includes('card-scrolling')) this.cardScrollingEffect();
      if (this.animations.includes('zoom-on-scroll')) this.zoomInOut();
      if (this.animations.includes('footer')) this.animateFooter();
    }

    adjustCalculations() {
      if (this.animations.includes('card-scrolling')) {
        this.currentSectionTop = this.shopifySection.getBoundingClientRect().top;
        if (this.nextSection) {
          this.nextSectionTop = this.nextSection.getBoundingClientRect().top;
          this.sectionOffsetTop = this.nextSectionTop - this.containerHeight - this.cardScrollingMarginTop;
        } else {
          this.nextSectionTop = this.currentSectionTop + this.containerHeight;
          this.sectionOffsetTop = this.currentSectionTop;
        }
        this.distance = Math.floor(this.currentSectionTop - this.headerHeight);
        this.after = Math.round(this.nextSectionTop - this.headerHeight - this.cardScrollingMarginTop + 0.1 * this.viewportHeight) < 0;
        this.isInView = this.isTopOfElementReached && !this.after;
      }
      if (this.animations.includes('footer')) {
        if (window.Shopify.designMode) this.assignFooterArguments();
        this.elementOffsetTopPoint = Math.round(this.prevSection?.getBoundingClientRect().bottom - this.footerPrevSectionOffset + this.scrollTop);
        this.elementOffsetBottomPoint = this.elementOffsetTopPoint + this.containerHeight;
        this.isBottomOfElementPassed = this.elementOffsetBottomPoint < this.scrollTop;
        this.isTopOfElementReached = this.elementOffsetTopPoint < this.scrollBottom;
        this.isInView = this.isTopOfElementReached && !this.isBottomOfElementPassed;
        this.isApproaching = Math.round(this.elementOffsetTopPoint - this.viewportHeight / 2) < this.scrollBottom;
      }
    }

    toggleVisibility() {
      if (this.animations.includes('card-scrolling')) {
        this.setClassState(this.shopifySection, 'card-scrolling-hidden', this.after, 'cardScrollingHidden');
      }
      if (this.animations.includes('footer')) {
        this.setClassState(this.shopifySection, 'section-footer--visible', Boolean(this.isApproaching || this.isInView), 'footerVisible');
      }
    }

    cardScrollingEffect() {
      this.setStyleProperty('--card-scrolling-overlay', this.opacityValue(this.sectionOffsetTop / this.containerHeight * -100));
    }

    zoomInOut() {
      if (this.animations.includes('card-scrolling') && this.nextSection) {
        const sectionTop = this.nextSectionTop - this.headerHeight - this.cardScrollingMarginTop - this.containerHeight;
        const sectionOffset = Math.round(sectionTop + this.scrollTop);
        const scrollProgress = this.scrollBottom - sectionOffset;
        this.percentageVisible = Number(100 * scrollProgress / (this.viewportHeight + this.containerHeight + this.cardScrollingMarginTop)).toFixed(2);
      }
      const scaleOffset = 0.2 * this.percentageVisible / 100;
      let scale = Number(1 + scaleOffset).toFixed(4);
      scale = scale > 1 ? scale : 1;
      this.setStyleProperty('--scale', scale);
    }

    offsetX() {
      const intensity = this.getAttribute('data-parallax-intensity') || 100;
      let initialOffset = this.animations.includes('offset-ltr') ? intensity : 0;
      if (this.hasAttribute('data-parallax-static')) initialOffset = intensity / 2;
      const direction = this.animations.includes('offset-ltr') ? 1 : -1;
      const position = Number((intensity * this.percentageVisible / 100 - initialOffset) * direction).toFixed(2);
      this.setStyleProperty('--offsetX', `${position}%`);
    }

    updateHeight() {
      if (this.animations.includes('card-scrolling')) {
        const {stickyHeaderHeight} = window.theme.readHeights();
        this.headerHeight = stickyHeaderHeight || 0;
        this.cardScrollingStickyTop = this.headerHeight || 0;
        this.cardScrollingMarginTop = this.nextSection ? parseInt(window.getComputedStyle(this.nextSection).getPropertyValue('margin-top')) : 0;
        this.containerHeight = this.container.offsetHeight;
        this.prevSection = this.shopifySection.previousElementSibling;
        this.updateStickyTopClass();
      }
      const isDesktop = matchMedia('(min-width: 1024px)').matches;
      const orientation = window.theme.getScreenOrientation();
      if (this.orientation !== orientation || isDesktop) {
        this.viewportHeight = Math.round(Math.max(document.documentElement.clientHeight, window.innerHeight || 0));
        this.containerHeight = this.container.offsetHeight;
        this.orientation = orientation;
      }
    }

    animateCircleText() {
      const rotation = 70 * this.percentage / 100 * -1;
      if (this.percentage > 0) this.setStyleProperty('--rotate', `${35 + rotation}deg`);
    }

    animateOverlappingImages() {
      const intensity = 0.25 * this.getAttribute('data-parallax-intensity');
      const midpoint = intensity / 2;
      const progress = intensity * this.percentage / 100;
      let primaryX = 0;
      let primaryY = 0;
      let secondaryX = 0;
      let secondaryY = 0;
      if (this.animations.includes('horizontal') || this.animations.includes('diagonal')) {
        primaryX = -midpoint + progress;
        secondaryX = midpoint - progress;
      }
      if (this.animations.includes('vertical') || this.animations.includes('diagonal')) {
        primaryY = midpoint - progress;
        secondaryY = -midpoint + progress;
      }
      this.setStyleProperty('--transformX-primary', `${primaryX}%`);
      this.setStyleProperty('--transformY-primary', `${primaryY}%`);
      if (!this.hasAttribute('data-parallax-single')) {
        this.setStyleProperty('--transformX-secondary', `${secondaryX}%`);
        this.setStyleProperty('--transformY-secondary', `${secondaryY}%`);
      }
    }

    animateFooter() {
      const offset = this.footerOffset * this.percentageFull;
      const percentage = Number(offset / this.footerOffset * 100).toFixed(2);
      let position = Number(this.footerOffset - offset).toFixed(2);
      if (this.percentageFull >= 1) position = 0;
      if (this.cachedPosition === position || this.cachedPercentage === percentage) return;
      this.setStyleProperty('--transformY', `${position}`);
      this.cachedPercentage = percentage;
      this.cachedPosition = position;
    }

    disconnectedCallback() {
      document.removeEventListener('theme:scroll', this.scrollEvent);
      document.removeEventListener('theme:resize', this.resizeEvent);
      document.removeEventListener('theme:parallax:refresh', this.refreshEvent);
    }

    refresh() {
      this.cachedStyles.clear();
      this.cardScrollingHidden = null;
      this.footerVisible = null;
      this.stickyTopZero = null;
      this.assignFooterArguments();
      this.assignCardScrollingArguments();
      requestAnimationFrame(() => this.updateParallax(true));
    }
  }

  customElements.define('parallax-element', ParallaxElement);
})();
